import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Slider from '@material-ui/core/Slider';
import ErrorIcon from '@material-ui/icons/Error';
import classnames from 'classnames';


const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  name: {
    fontSize: theme.typography.h6.fontSize,
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
  },
  value: {
    fontSize: theme.typography.h1.fontSize,
    textAlign: 'center',
    color: theme.palette.facet.accentBlue4,
    minHeight: theme.spacing(8),
    fontWeight: theme.typography.fontWeightBold,
  },
  valueReadonly: {
    color: theme.palette.facet.mediumDarkGray,
  },
  sliderContainer: {
    maxWidth: theme.dimensions.md,
    width: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  sliderRoot: {
    height: 5,
  },
  sliderRail: {
    height: 5,
    backgroundImage: `linear-gradient(0.25turn, #72DAB4, #58AAE0, ${theme.palette.facet.accentBlue4})`,
    opacity: 1,
  },
  sliderTrack: {
    height: 5,
    background: 'transparent',
  },
  sliderThumb: {
    height: 19,
    width: 19,
    marginTop: -7,
  },
  warningContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: -theme.spacing(2),
  },
  warningIconContainer: {
    display: 'flex',
    marginRight: theme.spacing(2),
    color: theme.palette.warning.main,
    padding: theme.spacing(0.75),
    borderRadius: '50%',
    backgroundColor: theme.palette.facet.accentYellow1,
  },
  warningIcon: {
    fontSize: theme.typography.h5.fontSize,
  },
  warningMessage: {
    color: theme.palette.facet.primaryGray,
  },
});

function RiskToleranceValue({
  name,
  value,
  warning,
  readonly,
  metaOptions,
  onValueChange,
  className,
  classes,
  ...props
}) {
  const [displayValue, setDisplayValue] = useState('');
  useEffect(() => {
    const optionFound = metaOptions.options.find(option => option.id === value);
    if (optionFound) {
      setDisplayValue(optionFound.prompt);
    }
  }, [value]);

  const [sliderOptionsByValue, setSliderOptionsByValue] = useState({});
  useEffect(() => {
    if (metaOptions?.options) {
      const newOptions = metaOptions.options.reduce((optionsMap, option, index) => ({
        ...optionsMap,
        [option.id]: index,
      }), {});
      setSliderOptionsByValue(newOptions);
    }
  }, [metaOptions?.options]);

  const [sliderValue, setSliderValue] = useState(0);
  useEffect(() => {
    setSliderValue(sliderOptionsByValue[value] || 0);
  }, [value, sliderOptionsByValue]);

  function handleChange(_, idx) {
    if (onValueChange) {
      onValueChange(metaOptions.options[idx].id);
    }
  }

  return (
    <div className={classnames(classes.root, className)} {...props}>
      <Typography className={classes.name}>{name}&apos;s Risk Tolerance</Typography>
      <Typography
        className={classnames(classes.value, {
          [classes.valueReadonly]: readonly,
        })}
      >
        {displayValue}
      </Typography>
      {!readonly
        ? <div className={classes.sliderContainer}><Slider
          classes={{
            root: classes.sliderRoot,
            rail: classes.sliderRail,
            track: classes.sliderTrack,
            thumb: classes.sliderThumb,
          }}
          min={0}
          max={metaOptions.options.length - 1}
          value={sliderValue}
          onChange={handleChange}
        /></div>
        : null}
      {warning ? (
        <div className={classnames(classes.warningContainer)}>
          <div className={classnames(classes.warningIconContainer)}><ErrorIcon className={classnames(classes.warningIcon)} /></div>
          <Typography className={classnames(classes.warningMessage)}>{warning}</Typography>
        </div>
      ) : null}
    </div>
  );
}

RiskToleranceValue.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  warning: PropTypes.string,
  readonly: PropTypes.bool.isRequired,
  metaOptions: PropTypes.object.isRequired,
  onValueChange: PropTypes.func,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

RiskToleranceValue.defaultProps = {
  className: undefined,
  onValueChange: undefined,
  warning: undefined,
};

export default withStyles(styles)(RiskToleranceValue);
